import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Group,
  Card,
  Text,
  Button,
  NumberInput,
  TextInput,
  Switch,
  Table,
  ActionIcon,
  Center,
  Loader,
  Tabs,
  Select,
  Collapse,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconTrash,
  IconInfoCircle,
  IconWorld,
  IconStethoscope,
  IconListDetails,
  IconPlus,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import AdminPageHeader from '../../components/AdminPageHeader';
import PageTransition from '../../components/animations/PageTransition';
import { referenciaEnarmService } from '../../services/referenciaEnarmService';
import { ReferenciaEnarm } from '../../Models/ReferenciaEnarm';
import {
  especialidadPerfilService,
  EspecialidadPerfilDTO,
  ReferenciaEnarmEspecialidadDTO,
} from '../../services/especialidadPerfilService';

const ReferenciaEnarmAdminPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();

  // ---- Tab activa
  const [tab, setTab] = useState<string | null>('global');
  const [infoOpen, setInfoOpen] = useState(false);

  // ---- Referencia global
  const [referencias, setReferencias] = useState<ReferenciaEnarm[]>([]);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [guardandoGlobal, setGuardandoGlobal] = useState(false);
  const [anio, setAnio] = useState<number>(new Date().getFullYear());
  const [promedio, setPromedio] = useState<number | ''>('');
  const [fuente, setFuente] = useState<string>('');
  const [activo, setActivo] = useState<boolean>(true);

  // ---- Catálogo de especialidades
  const [especialidades, setEspecialidades] = useState<EspecialidadPerfilDTO[]>([]);
  const [loadingEsp, setLoadingEsp] = useState(true);
  const [nuevoNombre, setNuevoNombre] = useState<string>('');
  const [guardandoEsp, setGuardandoEsp] = useState(false);

  // ---- Referencias por especialidad
  const [refsEsp, setRefsEsp] = useState<ReferenciaEnarmEspecialidadDTO[]>([]);
  const [loadingRefsEsp, setLoadingRefsEsp] = useState(true);
  const [refEspId, setRefEspId] = useState<string | null>(null);
  const [refEspAnio, setRefEspAnio] = useState<number>(new Date().getFullYear());
  const [refEspPromedio, setRefEspPromedio] = useState<number | ''>('');
  const [refEspFuente, setRefEspFuente] = useState<string>('');
  const [refEspActivo, setRefEspActivo] = useState<boolean>(true);
  const [guardandoRefEsp, setGuardandoRefEsp] = useState(false);

  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const colActiva = dark ? '#86b8a6' : '#4f8a74';
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };
  // Pestañas tipo "navbar" segmentado (estilo SegmentedControl de Notificaciones: poco redondo).
  const tabStyle = (v: string): React.CSSProperties => ({
    borderRadius: 8,
    padding: '6px 14px',
    fontWeight: 600,
    fontSize: 13,
    border: 'none',
    color: tab === v ? (dark ? '#1e293b' : '#f7f3ee') : mutedColor,
    backgroundColor: tab === v ? textColor : 'transparent',
  });
  const tabsListStyle: React.CSSProperties = {
    display: 'flex',
    width: 'fit-content',
    marginRight: 'auto',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: 4,
    padding: 4,
    borderRadius: 12,
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(237, 231, 222, 0.7)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(221, 216, 209, 0.8)'}`,
  };

  // ===== Cargas =====
  const cargarGlobal = async () => {
    try {
      setLoadingGlobal(true);
      const data = await referenciaEnarmService.listar();
      setReferencias(data);
    } catch (err) {
      console.error(err);
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudieron cargar las referencias globales' });
    } finally {
      setLoadingGlobal(false);
    }
  };

  const cargarEspecialidades = async () => {
    try {
      setLoadingEsp(true);
      const data = await especialidadPerfilService.listarTodasAdmin();
      setEspecialidades(data);
    } catch (err) {
      console.error(err);
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudo cargar el catálogo' });
    } finally {
      setLoadingEsp(false);
    }
  };

  const cargarRefsEsp = async () => {
    try {
      setLoadingRefsEsp(true);
      const data = await especialidadPerfilService.listarReferenciasAdmin();
      setRefsEsp(data);
    } catch (err) {
      console.error(err);
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudieron cargar promedios por especialidad' });
    } finally {
      setLoadingRefsEsp(false);
    }
  };

  useEffect(() => {
    cargarGlobal();
    cargarEspecialidades();
    cargarRefsEsp();
  }, []);

  // ===== Handlers global =====
  const handleGuardarGlobal = async () => {
    if (promedio === '' || isNaN(Number(promedio))) {
      notifications.show({ color: 'red', title: 'Datos incompletos', message: 'Ingresa un promedio válido (0-100)' });
      return;
    }
    if (Number(promedio) < 0 || Number(promedio) > 100) {
      notifications.show({ color: 'red', title: 'Valor inválido', message: 'El promedio debe estar entre 0 y 100' });
      return;
    }
    try {
      setGuardandoGlobal(true);
      await referenciaEnarmService.guardar({
        anio,
        promedio: Number(promedio),
        fuente: fuente.trim() || undefined,
        activo,
      });
      notifications.show({ color: 'teal', title: 'Guardado', message: `Referencia ENARM ${anio} guardada` });
      setPromedio('');
      setFuente('');
      await cargarGlobal();
    } catch (err) {
      console.error(err);
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudo guardar' });
    } finally {
      setGuardandoGlobal(false);
    }
  };

  const handleEliminarGlobal = async (id?: number) => {
    if (!id || !window.confirm('¿Eliminar esta referencia global?')) return;
    try {
      await referenciaEnarmService.eliminar(id);
      notifications.show({ color: 'teal', title: 'Eliminada', message: 'Referencia eliminada' });
      await cargarGlobal();
    } catch (err) {
      console.error(err);
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudo eliminar' });
    }
  };

  // ===== Handlers catálogo =====
  const handleAgregarEsp = async () => {
    if (!nuevoNombre.trim()) return;
    try {
      setGuardandoEsp(true);
      await especialidadPerfilService.guardar({ nombre: nuevoNombre.trim(), activo: true, orden: 99 });
      notifications.show({ color: 'teal', title: 'Añadida', message: 'Especialidad añadida' });
      setNuevoNombre('');
      await cargarEspecialidades();
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: err?.response?.data?.message || 'No se pudo añadir',
      });
    } finally {
      setGuardandoEsp(false);
    }
  };

  const handleToggleEsp = async (id: number) => {
    try {
      await especialidadPerfilService.toggleActivo(id);
      await cargarEspecialidades();
    } catch (err) {
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudo cambiar el estado' });
    }
  };

  const handleEliminarEsp = async (id: number) => {
    if (!window.confirm('¿Eliminar esta especialidad? También se eliminarán sus promedios asociados.')) return;
    try {
      await especialidadPerfilService.eliminar(id);
      notifications.show({ color: 'teal', title: 'Eliminada', message: 'Especialidad eliminada' });
      await cargarEspecialidades();
      await cargarRefsEsp();
    } catch (err) {
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudo eliminar' });
    }
  };

  // ===== Handlers refs por especialidad =====
  const handleGuardarRefEsp = async () => {
    if (!refEspId) {
      notifications.show({ color: 'red', title: 'Falta especialidad', message: 'Elige una especialidad' });
      return;
    }
    if (refEspPromedio === '' || isNaN(Number(refEspPromedio))) {
      notifications.show({ color: 'red', title: 'Datos incompletos', message: 'Ingresa un promedio válido' });
      return;
    }
    if (Number(refEspPromedio) < 0 || Number(refEspPromedio) > 100) {
      notifications.show({ color: 'red', title: 'Valor inválido', message: 'El promedio debe estar entre 0 y 100' });
      return;
    }
    try {
      setGuardandoRefEsp(true);
      await especialidadPerfilService.guardarReferencia({
        especialidadId: Number(refEspId),
        anio: refEspAnio,
        promedio: Number(refEspPromedio),
        fuente: refEspFuente.trim() || undefined,
        activo: refEspActivo,
      });
      notifications.show({ color: 'teal', title: 'Guardado', message: 'Promedio guardado' });
      setRefEspPromedio('');
      setRefEspFuente('');
      await cargarRefsEsp();
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: err?.response?.data?.message || 'No se pudo guardar',
      });
    } finally {
      setGuardandoRefEsp(false);
    }
  };

  const handleEliminarRefEsp = async (id: number) => {
    if (!window.confirm('¿Eliminar este promedio por especialidad?')) return;
    try {
      await especialidadPerfilService.eliminarReferencia(id);
      notifications.show({ color: 'teal', title: 'Eliminado', message: 'Promedio eliminado' });
      await cargarRefsEsp();
    } catch (err) {
      notifications.show({ color: 'red', title: 'Error', message: 'No se pudo eliminar' });
    }
  };

  const especialidadesOptions = especialidades
    .filter((e) => e.activo)
    .map((e) => ({ value: String(e.id), label: e.nombre }));

  return (
    <PageTransition type="medical" duration={800}>
    <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <AdminPageHeader
            title="Referencias ENARM"
            subtitle="Promedio global de referencia + promedios por especialidad para la comparativa del estudiante"
          />
          <Tooltip label="¿Cómo funciona la comparativa?" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={() => setInfoOpen((o) => !o)}
              aria-label="Información"
              style={{ color: infoOpen ? textColor : mutedColor }}
            >
              <IconInfoCircle size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Collapse in={infoOpen}>
          <Card withBorder radius="lg" p="md" style={cardStyle}>
            <Text size="sm" style={{ color: textColor }}>
              La <b>comparativa</b> de cada estudiante usa primero el promedio de <b>su especialidad de interés</b>.
              Si no hay promedio configurado para esa especialidad, cae al promedio <b>global</b>.
            </Text>
          </Card>
        </Collapse>

        <Tabs value={tab} onChange={setTab} variant="pills">
          <Tabs.List style={tabsListStyle}>
            <Tabs.Tab value="global" style={tabStyle('global')} leftSection={<IconWorld size={14} />}>Promedio global</Tabs.Tab>
            <Tabs.Tab value="especialidad" style={tabStyle('especialidad')} leftSection={<IconStethoscope size={14} />}>Por especialidad</Tabs.Tab>
            <Tabs.Tab value="catalogo" style={tabStyle('catalogo')} leftSection={<IconListDetails size={14} />}>Catálogo de especialidades</Tabs.Tab>
          </Tabs.List>

          {/* ============ TAB GLOBAL ============ */}
          <Tabs.Panel value="global" pt="md">
            <Stack gap="lg">
              <Card radius="lg" p="lg" style={cardStyle}>
                <Text fw={600} mb="md">Capturar / actualizar referencia global</Text>
                <Stack gap="md">
                  <Group grow align="flex-start">
                    <NumberInput
                      label="Año"
                      value={anio}
                      onChange={(v) => setAnio(Number(v) || new Date().getFullYear())}
                      min={2000}
                      max={2100}
                      allowDecimal={false}
                    />
                    <NumberInput
                      label="Promedio de referencia (%)"
                      value={promedio}
                      onChange={(v) => setPromedio(v === '' ? '' : Number(v))}
                      min={0}
                      max={100}
                      decimalScale={1}
                      placeholder="Ej. 62.5"
                    />
                  </Group>
                  <TextInput
                    label="Fuente"
                    value={fuente}
                    onChange={(e) => setFuente(e.currentTarget.value)}
                    placeholder="Ej. Resultados ENARM 2024 - CIFRHS"
                  />
                  <Switch
                    label="Marcar como activa (solo una activa a la vez)"
                    checked={activo}
                    onChange={(e) => setActivo(e.currentTarget.checked)}
                  />
                  <Group justify="flex-end">
                    <Button styles={primaryBtn} leftSection={<IconDeviceFloppy size={16} />} onClick={handleGuardarGlobal} loading={guardandoGlobal}>
                      Guardar
                    </Button>
                  </Group>
                </Stack>
              </Card>

              <Card radius="lg" p="lg" style={cardStyle}>
                <Text fw={600} mb="md">Referencias globales</Text>
                {loadingGlobal ? (
                  <Center py="xl"><Loader color="gray" /></Center>
                ) : referencias.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="lg">Aún no hay referencias</Text>
                ) : (
                  <Table verticalSpacing="sm" highlightOnHover style={{ textAlign: 'left' }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Año</Table.Th>
                        <Table.Th>Promedio</Table.Th>
                        <Table.Th>Fuente</Table.Th>
                        <Table.Th>Estado</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {referencias.map((r) => (
                        <Table.Tr key={r.id}>
                          <Table.Td>{r.anio}</Table.Td>
                          <Table.Td>{r.promedio?.toFixed(1)}%</Table.Td>
                          <Table.Td style={{ maxWidth: 320 }}>
                            <Text size="sm" c="dimmed" lineClamp={2}>{r.fuente || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            {r.activo ? (
                              <Text size="xs" fw={600} style={{ color: colActiva }}>Activa</Text>
                            ) : (
                              <Text size="xs" fw={600} style={{ color: mutedColor }}>Inactiva</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon color="red" variant="subtle" onClick={() => handleEliminarGlobal(r.id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* ============ TAB POR ESPECIALIDAD ============ */}
          <Tabs.Panel value="especialidad" pt="md">
            <Stack gap="lg">
              <Card radius="lg" p="lg" style={cardStyle}>
                <Text fw={600} mb="md">Capturar promedio por especialidad</Text>
                <Stack gap="md">
                  <Select
                    label="Especialidad"
                    placeholder="Elige una"
                    data={especialidadesOptions}
                    value={refEspId}
                    onChange={setRefEspId}
                    searchable
                  />
                  <Group grow align="flex-start">
                    <NumberInput
                      label="Año"
                      value={refEspAnio}
                      onChange={(v) => setRefEspAnio(Number(v) || new Date().getFullYear())}
                      min={2000}
                      max={2100}
                      allowDecimal={false}
                    />
                    <NumberInput
                      label="Promedio (%)"
                      value={refEspPromedio}
                      onChange={(v) => setRefEspPromedio(v === '' ? '' : Number(v))}
                      min={0}
                      max={100}
                      decimalScale={1}
                      placeholder="Ej. 65.0"
                    />
                  </Group>
                  <TextInput
                    label="Fuente (opcional)"
                    value={refEspFuente}
                    onChange={(e) => setRefEspFuente(e.currentTarget.value)}
                    placeholder="Ej. ENARM 2024 - Pediatría"
                  />
                  <Switch
                    label="Activa"
                    checked={refEspActivo}
                    onChange={(e) => setRefEspActivo(e.currentTarget.checked)}
                  />
                  <Group justify="flex-end">
                    <Button styles={primaryBtn} leftSection={<IconDeviceFloppy size={16} />} onClick={handleGuardarRefEsp} loading={guardandoRefEsp}>
                      Guardar
                    </Button>
                  </Group>
                </Stack>
              </Card>

              <Card radius="lg" p="lg" style={cardStyle}>
                <Text fw={600} mb="md">Promedios capturados</Text>
                {loadingRefsEsp ? (
                  <Center py="xl"><Loader color="gray" /></Center>
                ) : refsEsp.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="lg">
                    Aún no hay promedios por especialidad. Captura el primero arriba.
                  </Text>
                ) : (
                  <Table verticalSpacing="sm" highlightOnHover style={{ textAlign: 'left' }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Especialidad</Table.Th>
                        <Table.Th>Año</Table.Th>
                        <Table.Th>Promedio</Table.Th>
                        <Table.Th>Fuente</Table.Th>
                        <Table.Th>Estado</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {refsEsp.map((r) => (
                        <Table.Tr key={r.id}>
                          <Table.Td>{r.especialidadNombre}</Table.Td>
                          <Table.Td>{r.anio}</Table.Td>
                          <Table.Td>{Number(r.promedio).toFixed(1)}%</Table.Td>
                          <Table.Td style={{ maxWidth: 280 }}>
                            <Text size="sm" c="dimmed" lineClamp={2}>{r.fuente || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            {r.activo ? <Text size="xs" fw={600} style={{ color: colActiva }}>Activa</Text> : <Text size="xs" fw={600} style={{ color: mutedColor }}>Inactiva</Text>}
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon color="red" variant="subtle" onClick={() => handleEliminarRefEsp(r.id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* ============ TAB CATÁLOGO ============ */}
          <Tabs.Panel value="catalogo" pt="md">
            <Stack gap="lg">
              <Card radius="lg" p="lg" style={cardStyle}>
                <Text fw={600} mb="md">Añadir especialidad al catálogo</Text>
                <Group align="flex-end">
                  <TextInput
                    label="Nombre"
                    value={nuevoNombre}
                    onChange={(e) => setNuevoNombre(e.currentTarget.value)}
                    placeholder="Ej. Nutrición clínica"
                    style={{ flex: 1 }}
                  />
                  <Button
                    styles={primaryBtn}
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAgregarEsp}
                    loading={guardandoEsp}
                    disabled={!nuevoNombre.trim()}
                  >
                    Añadir
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" mt="sm">
                  Las especialidades activas aparecen en el selector del perfil del estudiante.
                </Text>
              </Card>

              <Card radius="lg" p="lg" style={cardStyle}>
                <Text fw={600} mb="md">Especialidades ({especialidades.length})</Text>
                {loadingEsp ? (
                  <Center py="xl"><Loader color="gray" /></Center>
                ) : (
                  <Table verticalSpacing="sm" highlightOnHover style={{ textAlign: 'left' }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Nombre</Table.Th>
                        <Table.Th>Orden</Table.Th>
                        <Table.Th>Estado</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {especialidades.map((e) => (
                        <Table.Tr key={e.id}>
                          <Table.Td>{e.nombre}</Table.Td>
                          <Table.Td>{e.orden}</Table.Td>
                          <Table.Td>
                            <Switch
                              size="sm"
                              checked={e.activo}
                              onChange={() => handleToggleEsp(e.id)}
                              label={e.activo ? 'Activa' : 'Inactiva'}
                            />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon color="red" variant="subtle" onClick={() => handleEliminarEsp(e.id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Box>
    </PageTransition>
  );
};

export default ReferenciaEnarmAdminPage;
